import { useMemo } from 'react';
import { Text, Button, Stack, Box, Badge, SegmentedControl, Group } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { TimelineEntryCard } from './TimelineEntryCard';
import { IMAGE_SIZES } from './imageSize';
import type { ImageSize } from './imageSize';
import type { TimelineEntry } from '@/types/timeline';

interface TimelineFeedProps {
  /** The organism's tank; lets the photo viewer load a larger copy past 2x. */
  tankId?: string;
  entries: TimelineEntry[];
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEdit?: (entry: TimelineEntry) => void;
  onDelete?: (entryId: number) => void;
  /** Opens a photo in the caller's viewer; without it, each card's own lightbox. */
  onOpenPhoto?: (entry: TimelineEntry) => void;
  /** Acquisition date to show as the origin point of the timeline */
  acquisitionDate?: string;
  imageSize?: ImageSize;
  onImageSizeChange?: (size: ImageSize) => void;
}

interface MonthGroup {
  label: string;
  entries: TimelineEntry[];
}

function groupByMonth(entries: TimelineEntry[]): MonthGroup[] {
  const groups = new Map<string, TimelineEntry[]>();

  entries.forEach((entry) => {
    const date = new Date(entry.entry_date);
    const key = `${date.getFullYear()}-${String(date.getMonth()).padStart(2, '0')}`;

    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(entry);
  });

  // Sort by key descending (newest first) and return with labels
  return Array.from(groups.entries())
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([, groupEntries]) => {
      const date = new Date(groupEntries[0]!.entry_date);
      return {
        label: date.toLocaleDateString('en-GB', {
          month: 'long',
          year: 'numeric',
        }),
        entries: groupEntries,
      };
    });
}

function formatAcquisitionDate(dateString: string): string {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function TimelineFeed({
  tankId,
  entries,
  hasMore = false,
  onLoadMore,
  onEdit,
  onDelete,
  onOpenPhoto,
  acquisitionDate,
  imageSize = 'medium',
  onImageSizeChange,
}: TimelineFeedProps): JSX.Element {
  const monthGroups = useMemo(() => groupByMonth(entries), [entries]);
  const showAcquisition = !!acquisitionDate;

  if (entries.length === 0 && !showAcquisition) {
    return (
      <Box py="xl" ta="center">
        <Text size="sm" c="dimmed">
          No timeline entries yet. Add your first note above.
        </Text>
      </Box>
    );
  }

  return (
    <>
      {/* Image size toggle -- outside position:relative container so timeline line aligns correctly */}
      {entries.length > 0 && onImageSizeChange && (
        <Group justify="flex-end" mb="sm">
          <SegmentedControl
            size="xs"
            value={imageSize}
            onChange={(val) => onImageSizeChange(val as ImageSize)}
            data={IMAGE_SIZES}
            aria-label="Image size"
          />
        </Group>
      )}

    <Box style={{ position: 'relative' }}>
      {/* Vertical teal timeline line */}
      <Box
        data-testid="timeline-line"
        style={{
          position: 'absolute',
          left: 7,
          top: 20,
          bottom: hasMore ? 48 : 8,
          width: 2,
          backgroundColor: 'var(--mantine-color-teal-6)',
          opacity: 0.4,
        }}
      />

      <Stack gap="lg">
        {monthGroups.map((group) => (
          <Box key={group.label}>
            {/* Month header */}
            <Text
              size="xs"
              fw={600}
              c="dimmed"
              tt="uppercase"
              mb="sm"
              ml={28}
              style={{ letterSpacing: '0.05em' }}
            >
              {group.label}
            </Text>

            <Stack gap="md">
              {group.entries.map((entry) => (
                <Box
                  key={entry.id}
                  style={{
                    display: 'flex',
                    gap: 'var(--mantine-spacing-sm)',
                    alignItems: 'flex-start',
                  }}
                >
                  {/* Dot marker */}
                  <Box
                    style={{
                      width: 16,
                      minWidth: 16,
                      display: 'flex',
                      justifyContent: 'center',
                      paddingTop: 4,
                    }}
                  >
                    <Box
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: 'var(--mantine-color-teal-6)',
                        border:
                          '2px solid var(--rdh-card-bg, var(--mantine-color-body))',
                        zIndex: 1,
                      }}
                    />
                  </Box>

                  {/* Entry content */}
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <TimelineEntryCard
                      tankId={tankId}
                      entry={entry}
                      imageSize={imageSize}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onOpenPhoto={onOpenPhoto}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>

      {/* Load more button */}
      {hasMore && onLoadMore && (
        <Box ta="center" mt="lg" ml="xl">
          <Button
            variant="subtle"
            color="gray"
            size="sm"
            leftSection={<IconChevronDown size={16} />}
            onClick={onLoadMore}
          >
            Load more
          </Button>
        </Box>
      )}

      {/* Acquisition date - origin point of the timeline */}
      {showAcquisition && !hasMore && (
        <Box
          mt="lg"
          style={{
            display: 'flex',
            gap: 'var(--mantine-spacing-sm)',
            alignItems: 'flex-start',
          }}
        >
          {/* Dot marker - slightly larger for the origin point */}
          <Box
            style={{
              width: 16,
              minWidth: 16,
              display: 'flex',
              justifyContent: 'center',
              paddingTop: 4,
            }}
          >
            <Box
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: 'var(--mantine-color-teal-6)',
                border:
                  '2px solid var(--rdh-card-bg, var(--mantine-color-body))',
                zIndex: 1,
              }}
            />
          </Box>

          {/* Acquisition content */}
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text
              size="xs"
              fw={600}
              c="dimmed"
              style={{
                fontFamily: 'Space Grotesk, sans-serif',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatAcquisitionDate(acquisitionDate!)}
            </Text>
            <Badge
              variant="light"
              color="teal"
              size="sm"
              mt={4}
            >
              Acquired
            </Badge>
          </Box>
        </Box>
      )}
    </Box>
    </>
  );
}
